import { useNavigate } from 'react-router-dom';
import { useReveal } from '@/hooks/useReveal';
import PageLoader from '@/components/PageLoader';
import HeroCrumbs from '@/components/HeroCrumbs';
import Button from '@/components/ui/Button';
// [2026-10-23 사용자] 제품 서브 화이트 전환(격리) — 다크 공유 모듈 대신 화이트 전용 모듈. 홈·InstallPages 영향 없음.
import s from './ProductWhite.module.scss';
// [2026-10-23 사용자] ③ 연결구성=셰브론(w.chevron), ④~⑦ 화면UI=AGENDA 스타일(s.agenda). t(buy/link카드)·Ic는 더 이상 미사용이라 제거.
import w from './SafetyEnv.module.scss';
import { GUIDE_ICON } from '@/components/GuideIcons';

// ============================================================================
//  제품 상세 — 모니터·TV 연결 중계기  (/products/display-repeater)
//  [2026-10-08] 기획서 v7 2-5. 대표제목(슬로건)·10섹션 순서·연결 이름을 문서 그대로.
//    개발 중 제품 → 외관·화면UI·사양은 전부 '준비중' placeholder, 섹션은 문서 순서로 전부 배치 + 실무 설명.
//    섹션: ①제품첫화면 ②사용목적 ③연결구성 ④평상시UI ⑤위험UI ⑥다수장비UI ⑦장애UI ⑧관리자설정 ⑨제품사양 ⑩출시·구매
//    연결(문서): ①사전문의 ②안전솔루션 ③플랫폼구조 ④~⑥화면예시 ⑦운영안내 ⑧플랫폼설정 ⑨사양표 ⑩문의
//    주의(기획서 block 149): 대시보드 전체를 TV에 축소표시 X — 작업자가 멀리서 즉시 판단할 정보만 크게.
//  히어로=안전 도메인 이미지(인접 관련), 출시 전이므로 '개발 중' 표기.
// ============================================================================

const HERO_IMG = '/images/products/products-hero.webp'; // [2026-10-11] 제품 메뉴 히어로 이미지 통일(구 hero-safety에서 변경)
// [2026-10-11 사용자] 서브 슬로건 풍성화(문서 대표제목 2-5 핵심 유지+확장). 히어로 중앙 충분히 차게.
const SLOGAN = '체감온도와 위험단계를 현장의 TV·모니터에 크게 실시간 표시해, 다수의 작업자가 한눈에 위험을 인지하고 대응하도록 돕는 현장 표시 중계기입니다.';
const INQ = '/support/inquiry?product=' + encodeURIComponent('모니터·TV 중계기') + '&from=/products/display-repeater';

const Reveal = ({ className = '', children }: { className?: string; children: React.ReactNode }) => {
  const ref = useReveal<HTMLDivElement>();
  return <div className={`${className} anim-scroll`} ref={ref}>{children}</div>;
};
function Soon({ label }: { label: string }) {
  return (
    <div className={s.soon} role="img" aria-label={`${label} 준비중`}>
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      <span>{label} · 준비중</span>
    </div>
  );
}

export default function ProductDisplayRepeater() {
  const navigate = useNavigate();
  const nav = (to: string) => navigate(to);

  return (
    <main className={s.page}>
      <PageLoader />

      <header className={s.hero}>
        <div className={s.heroBg} style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className={s.heroOverlay} />
        <HeroCrumbs items={['HOME', '제품', '모니터·TV 중계기']} />
        <div className={s.heroInner}>
          <p className={s.heroEyebrow}>DISPLAY REPEATER · 개발 중</p>
          <h1 className={s.heroTitle}>모니터·TV 연결 중계기</h1>
          <p className={s.heroSub}>{SLOGAN}</p>
        </div>
      </header>

      {/* ① 제품 첫 화면 → 사전 문의 */}
      <section className={s.section}>
        <Reveal className={s.first}>
          <div className={s.firstMedia}><Soon label="중계기 외관·렌더링" /></div>
          <div className={s.firstBody}>
            <span className={s.badge}>개발 중 · 출시 예정</span>
            <h2 className={s.h2}>모니터·TV 연결 중계기</h2>
            <p className={s.lead}>체감온도계가 측정한 현장 위험정보를 TV·모니터에 크게 표시하는 중계기를 개발하고 있습니다. 휴대전화 없이도 작업자가 공동 화면으로 위험상태를 즉시 확인합니다.</p>
            <div className={s.ctaRow}>
              <Button variant="pill" onClick={() => nav(INQ)}>사전 문의</Button>
              <a className={s.ctaGhost} onClick={() => nav(INQ)}>출시 알림 신청</a>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ② 사용 목적 → 안전 솔루션 */}
      <section className={`${s.section} ${s.alt}`}>
        <Reveal>
          <p className={s.eyebrow}>PURPOSE</p>
          <h2 className={s.h2c}>휴대전화 없이, 공동 화면으로 위험 확인</h2>
          <p className={s.sub}>다수 작업자가 함께 보는 현장 TV·모니터에 위험상태를 크게 표시해, 개인 단말 없이도 모두가 즉시 인지하도록 합니다.</p>
          <a className={s.moreLink} onClick={() => nav('/safety')}>안전 솔루션 보기 →</a>
        </Reveal>
      </section>

      {/* ③ 연결 구성 → 플랫폼 구조 */}
      <section className={s.section}>
        <Reveal>
          <p className={s.eyebrow}>CONNECTION</p>
          <h2 className={s.h2c}>연결 구성</h2>
          {/* [2026-10-23 사용자] flow5 → evs-g200 ② 셰브론(맞물린 컬러 화살표)으로 통일 */}
          <div className={`${w.chevron} ${w.chevron5}`}>
            {[
              // [2026-10-23 사용자] 아이콘 의미 재검토(실측 반영: GUIDE_ICON 'monitor'=지구본, 'display'=화면):
              //   체감온도계=계측(gauge)·클라우드=네트워크(monitor 지구본)·중계기=장비(gateway)·HDMI=출력(signal)·TV=화면(display)
              { n: '01', t: '체감온도계', i: 'gauge', d: '현장 측정', c: w.cBlue },
              { n: '02', t: '클라우드', i: 'monitor', d: '위험 판정', c: w.cTeal },
              { n: '03', t: '중계기', i: 'gateway', d: '화면 신호 생성', c: w.cIndigo },
              { n: '04', t: 'HDMI', i: 'signal', d: '영상 출력', c: w.cAmber },
              { n: '05', t: 'TV·모니터', i: 'display', d: '현장 표시', c: w.cCoral },
            ].map((x) => (
              <div key={x.n} className={`${w.chevronStep} ${x.c}`}>
                <div className={w.chevronIco}>{GUIDE_ICON[x.i]}</div>
                <span className={w.chevronNum}>{x.n}</span>
                <h3>{x.t}</h3><p>{x.d}</p>
              </div>
            ))}
          </div>
          <a className={s.moreLink} onClick={() => nav('/safety/platform')}>플랫폼 구조 →</a>
        </Reveal>
      </section>

      {/* ④~⑦ 화면 UI — [2026-10-23 사용자] AGENDA 스타일(전폭 그라데이션 + 큰 번호 지그재그). 그라데이션=이미지44 PIL 샘플. '준비중' 제거 */}
      <section className={`${s.section} ${s.agenda}`}>
        <Reveal>
          <p className={s.agendaEyebrow}>SCREEN UI</p>
          <h2 className={s.agendaHead}>화면 UI</h2>
          <p className={s.agendaSub}>작업자가 멀리서 즉시 판단할 정보만 크게 표시합니다. 대시보드 전체를 축소해 띄우지 않습니다.</p>
          <div className={s.agendaGrid}>
            {[
              { n: '01', t: '평상시 UI', d: '현장명·공정명·체감온도·온습도·장비상태·수신시간을 표시합니다.' },
              { n: '02', t: '위험 UI', d: '전체화면 경고로 단계·문구·아이콘·행동요령을 표시하고 음향과 연계합니다.' },
              { n: '03', t: '다수 장비 UI', d: '자동 순환·목록형으로 표시하며 최고위험 장비를 우선 표시합니다.' },
              { n: '04', t: '장애 UI', d: '통신중단 문구·마지막 수신시간을 표시하고 화면 고정을 방지합니다.' },
            ].map((x, i) => (
              <div key={x.n} className={`${s.agendaCol} ${i % 2 === 1 ? s.agendaLow : ''}`}>
                <span className={s.agendaNum}>{x.n}</span>
                <span className={s.agendaLine} />
                <div>
                  <div className={s.agendaTitle}>{x.t}</div>
                  <div className={s.agendaDesc}>{x.d}</div>
                </div>
              </div>
            ))}
          </div>
          {/* [2026-10-23 사용자] '실제 TV 화면 UI 예시 · 준비중'은 유지(제거 아님). 그라데이션 위 흰 점선. */}
          <Soon label="실제 TV 화면 UI 예시" />
        </Reveal>
      </section>

      {/* ⑧ 관리자 설정 → 플랫폼 설정 */}
      <section className={s.section}>
        <Reveal>
          <p className={s.eyebrow}>ADMIN</p>
          <h2 className={s.h2c}>관리자 설정</h2>
          <p className={s.sub}>표시 공정·순환 시간·문구·로고·화면 템플릿을 관리자가 설정합니다.</p>
          <a className={s.moreLink} onClick={() => nav('/safety/platform')}>플랫폼 설정 →</a>
        </Reveal>
      </section>

      {/* ⑨ 제품 사양 → 사양표 */}
      <section className={`${s.section} ${s.alt}`}>
        <Reveal>
          <p className={s.eyebrow}>SPEC</p>
          <h2 className={s.h2c}>제품 사양</h2>
          <p className={s.sub}>HDMI·해상도·Ethernet·Wi-Fi·LTE·전원·자동부팅·업데이트 등 확정 하드웨어 사양을 게시합니다.</p>
          <Soon label="확정 하드웨어 사양표" />
        </Reveal>
      </section>

      {/* ⑩ 출시·구매 → 문의 · [2026-10-23] 위 ⑨가 alt(soft)라 결론 CTA는 흰 배경으로(인접 대비) */}
      <section className={`${s.section} ${s.finalCta} ${s.ctaWhite}`}>
        <Reveal>
          <h2 className={s.h2c}>출시 소식을 먼저 받아보세요</h2>
          <p className={s.sub}>개발 상태·예상 출시일을 안내해 드립니다. 사전 문의 또는 출시 알림을 신청하세요.</p>
          <div className={s.ctaRow}>
            <Button variant="pill" onClick={() => nav(INQ)}>사전 문의</Button>
            <a className={s.ctaGhost} onClick={() => nav(INQ)}>출시 알림 신청</a>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
